import type { MouseEventHandler, ReactNode, TransitionEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import eases from 'styles/tools/eases'
import zIndex from 'styles/tools/z-index'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Snackbar, { type SnackbarKind } from 'LuxKit/Snackbar/Snackbar'
import { headerHeight } from 'tripPlanner/config'

const TRIP_PLANNER_SNACKBAR_EVENT = 'trip-planner-snackbar'
const TRIP_PLANNER_SNACKBAR_DISMISS_PENDING_EVENT = 'trip-planner-snackbar-dismiss-pending'

const DISMISS_DELAY_MS = 4000
const ACTIONABLE_DISMISS_DELAY_MS = 8000
/**
 * Pending snackbars wait on the assistant, which routinely takes far longer than a normal
 * toast lives. They stay up until their result replaces them; this is only a safety net so
 * a dropped stream cannot leave a spinner on screen forever.
 */
export const PENDING_TIMEOUT_MS = 60000

interface TripPlannerSnackbarAction {
  label: string
  onAction: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>
  testId?: string
}

export interface TripPlannerSnackbarConfig {
  description: string
  kind: SnackbarKind
  heading?: string
  icon?: ReactNode
  action?: TripPlannerSnackbarAction
  /** Keep showing until another snackbar replaces this one. For in-flight work. */
  pending?: boolean
}

/**
 * Trip planner snackbars sit under the header rather than at the bottom of the viewport,
 * so they cannot go through the app-wide AppSnackbar. Showing a second one replaces the
 * first immediately, which is what drives the "Adding to trip" → "Itinerary updated" handover.
 */
export function showTripPlannerSnackbar(config: TripPlannerSnackbarConfig) {
  document.dispatchEvent(
    new CustomEvent<TripPlannerSnackbarConfig>(TRIP_PLANNER_SNACKBAR_EVENT, { detail: config }),
  )
}

/**
 * Closes a pending snackbar whose work ended without a result to show — e.g. the assistant
 * answered without touching the itinerary. No-op when nothing pending is on screen.
 */
export function dismissPendingTripPlannerSnackbar() {
  document.dispatchEvent(new CustomEvent(TRIP_PLANNER_SNACKBAR_DISMISS_PENDING_EVENT))
}

const SnackbarViewport = styled.div`
  position: fixed;
  inset-inline: 0;
  top: calc(${headerHeight.sm} + ${rem(8)});
  z-index: ${zIndex.notification};
  display: flex;
  justify-content: center;
  padding-inline: ${rem(8)};
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    top: calc(${headerHeight.lg} + ${rem(16)});
    justify-content: flex-end;
    padding-inline: ${rem(16)};
  }
`

const AnimatedSnackbar = styled.div`
  width: 100%;
  opacity: 0;
  transform: translateY(-150%);
  transition: transform 0.3s ${eases.power2.in}, opacity 0.3s ${eases.circ.in};
  pointer-events: none;

  ${mediaQueryUp.tablet} {
    width: auto;
    max-width: ${rem(500)};
    min-width: ${rem(360)};
  }

  &.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
    transition: transform 0.3s ${eases.power2.out}, opacity 0.3s ${eases.circ.out};
  }
`

function TripPlannerSnackbarHost() {
  const [config, setConfig] = useState<TripPlannerSnackbarConfig>()
  const [open, setOpen] = useState(false)
  const timeoutRef = useRef<number>(undefined)
  const isPendingRef = useRef(false)
  const elementRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onShow = (event: Event) => {
      const nextConfig = (event as CustomEvent<TripPlannerSnackbarConfig>).detail
      window.clearTimeout(timeoutRef.current)
      isPendingRef.current = !!nextConfig.pending
      setConfig(nextConfig)
      setOpen(true)
      const dismissDelay = nextConfig.pending
        ? PENDING_TIMEOUT_MS
        : nextConfig.action ? ACTIONABLE_DISMISS_DELAY_MS : DISMISS_DELAY_MS
      timeoutRef.current = window.setTimeout(() => setOpen(false), dismissDelay)
    }

    const onDismissPending = () => {
      if (!isPendingRef.current) return
      isPendingRef.current = false
      window.clearTimeout(timeoutRef.current)
      setOpen(false)
    }

    document.addEventListener(TRIP_PLANNER_SNACKBAR_EVENT, onShow)
    document.addEventListener(TRIP_PLANNER_SNACKBAR_DISMISS_PENDING_EVENT, onDismissPending)

    return () => {
      document.removeEventListener(TRIP_PLANNER_SNACKBAR_EVENT, onShow)
      document.removeEventListener(TRIP_PLANNER_SNACKBAR_DISMISS_PENDING_EVENT, onDismissPending)
      window.clearTimeout(timeoutRef.current)
    }
  }, [])

  const dismiss = useCallback(() => {
    window.clearTimeout(timeoutRef.current)
    timeoutRef.current = undefined
    setOpen(false)
  }, [])

  // Keep the closed snackbar mounted until it has animated out, otherwise it disappears
  // instantly instead of sliding back up under the header.
  const onTransitionEnd = useCallback((event: TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName === 'transform' && event.target === elementRef.current && !open) {
      setConfig(undefined)
    }
  }, [open])

  const onActionClick = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.stopPropagation()
    dismiss()
    config?.action?.onAction(event)
  }, [config?.action, dismiss])

  return (
    <SnackbarViewport data-testid="trip-planner-snackbar-host">
      <AnimatedSnackbar
        ref={elementRef}
        className={clsx({ open })}
        onTransitionEnd={onTransitionEnd}
      >
        {config && <Snackbar
          heading={config.heading}
          description={config.description}
          kind={config.kind}
          icon={config.icon}
          action={config.action?.label}
          onAction={onActionClick}
          actionTestId={config.action?.testId}
          onClick={dismiss}
        />}
      </AnimatedSnackbar>
    </SnackbarViewport>
  )
}

export default TripPlannerSnackbarHost
